// Hook: fetch per-WO or per-tree progress.
//
// Two modes:
//   useProgressV2({ orderLineId }) — calls GET /api/v2/order-lines/{id}/progress/
//     returns the backend payload: { tree_id, top_level, by_wo, totals }
//   useProgressV2({ workOrder })   — computes a per-WO progress object from
//     a WorkOrderV2 record (without a server round-trip):
//       { planned, produced, scrapped, percent_complete }
//
// Both modes also expose loading / error / refresh.
import { useCallback, useEffect, useRef, useState } from 'react';
import { v2 } from '../../../../services/api';

function toNumber(v) {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

export function calcProgressForWO(wo) {
  if (!wo) return { planned: 0, produced: 0, scrapped: 0, percent_complete: 0 };
  const planned = toNumber(wo.quantity_to_produce);
  // Sum step-level production if available; else fall back to recent_movements
  let produced = 0;
  let scrapped = 0;
  if (Array.isArray(wo.steps) && wo.steps.length > 0) {
    // Use the final step's completed qty as the WO's produced output.
    const sorted = [...wo.steps].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    const last = sorted[sorted.length - 1];
    produced = toNumber(last?.quantity_completed);
    scrapped = sorted.reduce((acc, s) => acc + toNumber(s.quantity_scrapped), 0);
  }
  if (Array.isArray(wo.recent_movements)) {
    for (const m of wo.recent_movements) {
      if (m.movement_type === 'receipt' && (m.item === wo.output_item)) {
        // already accounted for via steps; leave alone unless steps absent
      }
    }
  }
  const percent = planned > 0 ? Math.min(100, (produced / planned) * 100) : 0;
  return {
    planned,
    produced,
    scrapped,
    percent_complete: percent,
  };
}

export function useProgressV2({ orderLineId, workOrder } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchTreeProgress = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const res = await v2.orderLines.progress(id);
      if (mountedRef.current) setData(res.data);
      return res.data;
    } catch (err) {
      console.error('useProgressV2: tree fetch failed', err);
      if (mountedRef.current) setError(err);
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (orderLineId != null) {
      fetchTreeProgress(orderLineId);
    } else if (workOrder) {
      setData(calcProgressForWO(workOrder));
    } else {
      setData(null);
    }
  }, [orderLineId, workOrder, fetchTreeProgress]);

  const refresh = useCallback(() => {
    if (orderLineId != null) return fetchTreeProgress(orderLineId);
    if (workOrder) {
      const p = calcProgressForWO(workOrder);
      setData(p);
      return Promise.resolve(p);
    }
    return Promise.resolve(null);
  }, [orderLineId, workOrder, fetchTreeProgress]);

  return { data, loading, error, refresh };
}

export default useProgressV2;
